import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTable, Column } from 'react-table';
import { Link } from 'react-router-dom';
import Paging from '../../common/layouts/page/Paging';
import { getPostCard, getAdminPostCardList } from "../../api";


interface PostCardTableProps {
    postList?: any[]; // 외부에서 전달받는 데이터 (선택적)
}

const PostCardTable: React.FC<PostCardTableProps> = ({ postList }) => {
    const [posts, setPosts] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchPosts = useCallback(async () => {
        if (postList !== undefined) {
            setPosts(postList);
            setTotalPages(1);
            return;
        }

        try {
            const data = await getAdminPostCardList(currentPage, 10);
            //  console.log("data:::", data)

            setPosts(data.content || []);
            setTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error("엽서 목록 로드 실패:", error);
        }
    }, [currentPage, postList]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false // 24시간 형식
        }).replace(/\./g, '-').replace(/\s/g, ' ').replace(/- /g, ' ');
    };

    const columns = useMemo(() => [
        {
            Header: '이미지',
            accessor: 'postCardPhoto',
            Cell: ({ value }: any) => (
                <div style={{ width: '40px', height: '60px', overflow: 'hidden', borderRadius: '4px' }}>
                    <img
                        src={`data:image/jpeg;base64,${value}`}
                        alt="postcard"
                        className="w-100 h-100 object-fit-cover"
                    />
                </div>
            )
        },
        {
            Header: '작가명',
            accessor: 'authorName',
        },
        {
            Header: '좋아요',
            accessor: 'viewCount',
            Cell: ({ row }: any) => (
                <span className="fs-12 text-muted">
                    ❤️{row.original.likeCount}
                </span>
            )
        },
        {
            Header: '등록일',
            accessor: 'createdAt',
            Cell: ({ value }: { value: string }) => (
                <span className="fs-13 text-muted">
                    {formatDate(value)}
                </span>
            )
        }
    ], []);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        columns,
        data: posts,
    });

    return (
        <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden">
            <div className="card-body p-0">
                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '120px' }}>
                    <table className="table table-hover mb-0" {...getTableProps()} style={{ minWidth: '700px' }}>
                        <thead style={{ backgroundColor: '#f8fafc' }}>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" {...column.getHeaderProps()}>{column.render('Header')}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map(cell => (
                                            <td className="px-4 py-3" {...cell.getCellProps()} style={{ verticalAlign: 'middle' }}>
                                                {cell.column.id === 'authorName' ? (
                                                    <Link to={`/postCardDetail`} state={{ post: row.original }} className="text-decoration-none fw-bold text-[#6366f1] hover:text-[#4f46e5]">
                                                        {cell.render('Cell')}
                                                    </Link>
                                                ) : cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-4 px-4">
                <Paging totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
            </div>
        </div>
    );
};

export default PostCardTable;